import { notaFiscalProduto } from "../../interfaces/notaFiscal/notaFiscalProduto";
import { TableBody } from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { StatusContext } from "../../store/StatusContextProvider";
import { ScannerContext } from "../../store/ScannerContextProvider";
import CustomDialog from "../CustomDialog/CustomDialog";
import { scannerTableDialogInformations } from "../../utils/messagesUtils";
import { DialogContext } from "../../store/DialogContextProvider";
import { FileContext } from "../../store/FileContextProvider";
import { isEmpty } from "../../utils/dataCheckUtils";
import CustomTableHeader from "./components/CustomTableHeader";
import CustomTableRow from "./components/CustomTableRow";
import CustomTableFooter from "./components/CustomTableFooter";
import formatDecimalNumber from "../../utils/numberUtils";
import * as Styles from "./CustomTable.styles";
import { composeUniqueKey, isSameProduct } from "../../utils/notaFiscalUtils";

interface tablePropsType {
  tableTitle: string;
  TableIcon: React.ElementType;
  primaryArray: notaFiscalProduto[];
  secondaryArray: notaFiscalProduto[];
  isScanMode?: boolean;
  isGettingCounter?: boolean;
  onResetScanner?: () => void;
}

export default function CustomTable({
  tableTitle,
  TableIcon,
  primaryArray = [],
  secondaryArray = [],
  isScanMode = false,
  isGettingCounter = false,
  onResetScanner,
}: tablePropsType) {
  const { updateScannerArray, scannerArray, handleFocusOnScanner, updateProductName, lastUpdatedProduct } = useContext(ScannerContext);
  const { calculateCounters, canEditProductScanner } = useContext(StatusContext);
  const { isDialogOpen, handleCloseDialog, handleOpenDialog } = useContext(DialogContext);
  const { notaFiscalProductsArray } = useContext(FileContext);
  const [selectedItem, setSelectedItem] = useState<notaFiscalProduto | null>(null);
  const [hasRemovedItem, setHasRemovedItem] = useState(false);
  const [lastUpdatedProductUniqueKey, setLastUpdatedProductUniqueKey] = useState<string>("");
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  const hasEmptyData = isEmpty<notaFiscalProduto>(primaryArray) || isEmpty<notaFiscalProduto>(secondaryArray);
  const hasAllData = !isEmpty<notaFiscalProduto>(primaryArray) && !isEmpty<notaFiscalProduto>(secondaryArray);
  const hasPrimaryData = !isEmpty<notaFiscalProduto>(primaryArray);
  const hasSecondaryData = !isEmpty<notaFiscalProduto>(secondaryArray);
  const showRemoveColumn = isScanMode && hasAllData;
  const tableColumnsTotal = isScanMode ? 7 : 6;

  useEffect(() => {
    if (lastUpdatedProduct === null || !isScanMode) return;

    const row = rowRefs.current[lastUpdatedProduct.rowKey];
    row?.focus();
    row?.scrollIntoView({
      block: "center",
      inline: "nearest",
    });
    setLastUpdatedProductUniqueKey(lastUpdatedProduct.rowKey);
  }, [lastUpdatedProduct, isScanMode]);

  //Calcular contadores para tabelas isGettingCounter ativo
  useEffect(() => {
    if (!isGettingCounter) return;
    else {
      if (primaryArray.length > 0 && secondaryArray.length > 0) {
        calculateCounters(primaryArray, secondaryArray, isGettingCounter);
      }
    }
  }, [isGettingCounter, primaryArray, secondaryArray, calculateCounters]);

  const { uniqueProducts, productQuantity, numberOfItems } = useMemo(() => {
    const unique: notaFiscalProduto[] = [];
    let quantity = 0;
    let items = 0;

    primaryArray.forEach((item) => {
      const uniqueKey = composeUniqueKey(item);
      const existingProduct = unique.find((prod) => composeUniqueKey(prod) === uniqueKey);

      if (existingProduct) {
        existingProduct.qCom += item.qCom;
      } else {
        unique.push({ ...item });
        items++;
      }
      quantity += item.qCom;
    });

    return { uniqueProducts: unique, productQuantity: quantity, numberOfItems: items };
  }, [primaryArray]);

  const productQuantityFormatted = formatDecimalNumber(productQuantity);

  function handleRemoveFromScanner(cEAN: string) {
    if (!isScanMode) return;

    const updatedScannerWithoutItem = scannerArray.filter((item) => item.cEAN !== cEAN);

    updateScannerArray(updatedScannerWithoutItem);
    setHasRemovedItem((prev) => (prev ? prev : true));
    calculateCounters(notaFiscalProductsArray, updatedScannerWithoutItem, true);
  }

  function handleOnBlinkAnimationEnd(): void {
    setLastUpdatedProductUniqueKey("");
  }

  function handleUpdateProductName(uniqueKey: string, newName: string) {
    updateProductName(uniqueKey, newName);
  }

  function handleResetScanner() {
    if (!isScanMode) return;
    onResetScanner?.();
  }

  return (
    <>
      <Styles.Container>
        <Styles.CustomTableContainer>
          <Styles.CustomTable aria-label="scrollable table" stickyHeader>
            <CustomTableHeader
              TableIcon={TableIcon}
              isScanMode={isScanMode}
              showRemoveColumn={showRemoveColumn}
              tableColumnsTotal={tableColumnsTotal}
              tableTitle={tableTitle}
              onResetScanner={handleResetScanner}
            />

            <TableBody>
              {uniqueProducts.map((row, index) => {
                const rowKey = composeUniqueKey(row);
                const isItemPresent = secondaryArray.some((scanner) => isSameProduct(scanner, row));

                const totalScannerQuantity = secondaryArray
                  .filter((scanner) => isSameProduct(scanner, row))
                  .reduce((total, scanner) => total + scanner.qCom, 0);

                const isItemTotalEqual = totalScannerQuantity === row.qCom;
                const rowQcomFormatted = formatDecimalNumber(row.qCom);

                return (
                  <CustomTableRow
                    key={rowKey}
                    row={row}
                    index={index}
                    isGettingCounter={isGettingCounter}
                    isItemPresent={isItemPresent}
                    isItemTotalEqual={isItemTotalEqual}
                    isScanMode={isScanMode}
                    showRemoveColumn={showRemoveColumn}
                    hasPrimaryData={hasPrimaryData}
                    hasSecondaryData={hasSecondaryData}
                    hasEmptyData={hasEmptyData}
                    hasRemovedItem={hasRemovedItem}
                    rowQcomFormatted={rowQcomFormatted}
                    lastUpdatedProductUniqueKey={lastUpdatedProductUniqueKey}
                    setSelectedItem={setSelectedItem}
                    rowRef={(element) => (rowRefs.current[rowKey] = element)}
                    handleOnBlinkAnimationEnd={handleOnBlinkAnimationEnd}
                    handleOpenDialog={handleOpenDialog}
                    onEditProductName={handleUpdateProductName}
                    isEditAllowed={canEditProductScanner(composeUniqueKey(row))}
                  />
                );
              })}
            </TableBody>
          </Styles.CustomTable>
        </Styles.CustomTableContainer>
        {hasPrimaryData && <CustomTableFooter numberOfItems={numberOfItems} productQuantityFormatted={productQuantityFormatted} />}
      </Styles.Container>
      {isScanMode && (
        <CustomDialog
          contentInformations={scannerTableDialogInformations}
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onConfirm={() => {
            if (selectedItem) {
              handleRemoveFromScanner(selectedItem.cEAN);
            }
            handleCloseDialog();
          }}
          onAfterClose={handleFocusOnScanner}
        />
      )}
    </>
  );
}
